import React, { useState } from 'react'
import { useMutation } from '@apollo/client'
import qs from 'query-string'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers'
import { useLocation } from 'react-router-dom'
import WizardForm from 'components/wizard-form'
import { phoneRegExp } from 'components/coaches-profile/constants'
import { useHideIntercom } from 'lib/support'
import { useSnackbar } from 'notistack'
import { PGA_COM_HOST } from 'env'
import { COACHING_LEAD_CREATE, ADD_QUESTIONNAIRE_TO_LEAD } from './query'
import ContactInfo from './steps/contact-info'
import CoachLooker from './steps/coach-looker'
import SelectAvailability from './steps/select-availability'
import Experience from './steps/select-experience'
import SelectGoals from './steps/select-goals'
import ShootingNumber from './steps/shooting-number'
import SelectPlan from './steps/select-plan'
import Summary from './steps/summary'
import { formatAnswers } from './utils'
import CoachAppbar from 'components/coach-app-bar'

const defaultValues = {
  questionnaire: [],
}

const validationSchema = () =>
  yup.object().shape({
    firstName: yup
      .string()
      .required()
      .max(50)
      .label('First Name'),
    lastName: yup
      .string()
      .required()
      .max(100)
      .label('Last Name'),
    phoneNumber: yup
      .string()
      .required()
      .matches(phoneRegExp, 'Your phone number must be 10 digits')
      .label('Phone Number'),
    email: yup
      .string()
      .email()
      .required()
      .max(100)
      .label('Email'),
    recaptchaToken: yup
      .string()
      .required()
      .min(2),
  })

const CoachingRequestFormWizard = () => {
  const location = useLocation()
  const { coach, source } = location.state
  const { returnTo } = qs.parse(location.search)
  const coachProfileUrl = `${PGA_COM_HOST}/coach/${coach?.coachProfile?.slug}`
  const [leadId, setLeadId] = useState(null)
  const { enqueueSnackbar } = useSnackbar()
  const [recaptchaToken, setRecaptchaToken] = useState('')
  useHideIntercom()

  const [addQuestionnaireToLead] = useMutation(ADD_QUESTIONNAIRE_TO_LEAD, {
    onCompleted: data => {
      if (data.addQuestionnaireToLead.success) {
        enqueueSnackbar(`${coach.name} received your request`, {
          variant: 'success',
        })
        window.location.href = coachProfileUrl
      } else {
        enqueueSnackbar(
          'We had trouble processing your request. Please try again.',
          { variant: 'error' },
        )
      }
    },
  })

  const [createCoachingLead, { error: leadCreateError }] = useMutation(
    COACHING_LEAD_CREATE,
    {
      context: { recaptchaToken },
      onError: () => {},
      onCompleted: (data, error) => {
        if (data.CoachingLeadCreate.success) {
          setLeadId(data.CoachingLeadCreate.newLeadId)
        } else {
          console.error('lead creation error')
        }
      },
    },
  )

  return (
    <>
      <CoachAppbar position="relative" hideContactButton />
      <WizardForm
        formProps={{
          defaultValues,
          resolver: yupResolver(validationSchema()),
        }}
        formName="coaching-request"
        steps={[
          {
            component: <CoachLooker coach={coach} />,
            fieldsToValidate: [],
            nextButtonLabel: 'Next',
            stepName: 'coach-looker',
          },
          {
            component: (
              <ContactInfo
                coach={coach}
                recaptchaToken={recaptchaToken}
                setRecaptchaToken={setRecaptchaToken}
                leadCreateError={leadCreateError}
              />
            ),
            fieldsToValidate: [
              'firstName',
              'lastName',
              'phoneNumber',
              'email',
              'recaptchaToken',
            ],
            nextButtonLabel: 'Next',
            stepName: 'contact-info',
            additionalAction: true,
          },
          {
            component: <SelectAvailability coach={coach} />,
            fieldsToValidate: [],
            nextButtonLabel: 'Next',
            stepName: 'availability',
          },
          {
            component: <SelectGoals coach={coach} />,
            fieldsToValidate: [],
            nextButtonLabel: 'Next',
            stepName: 'goal',
          },
          {
            component: <Experience coach={coach} />,
            fieldsToValidate: [],
            nextButtonLabel: 'Next',
            stepName: 'select-experience',
          },
          {
            component: <ShootingNumber coach={coach} />,
            fieldsToValidate: [],
            nextButtonLabel: 'Next',
            stepName: 'shooting-number',
          },
          {
            component: <SelectPlan coach={coach} />,
            fieldsToValidate: [],
            nextButtonLabel: 'Next',
            stepName: 'select-plan',
          },
          {
            component: <Summary coach={coach} />,
            fieldsToValidate: [],
            nextButtonLabel: 'Send to Coach',
            stepName: 'summary',
          },
        ]}
        onSubmit={async formValues => {
          const formattedAnswers = formatAnswers(formValues.questionnaire)
          return addQuestionnaireToLead({
            variables: {
              id: leadId,
              answers: formattedAnswers,
            },
          })
        }}
        onCancel={() => (window.location.href = returnTo || coachProfileUrl)}
        onAdditionalAction={async formValues => {
          return createCoachingLead({
            variables: {
              params: {
                firstName: formValues.firstName,
                lastName: formValues.lastName,
                phoneNumber: formValues.phoneNumber,
                email: formValues.email,
                coachSlug: coach.coachProfile.slug,
                source: source,
                intent: '',
              },
            },
          })
        }}
      />
    </>
  )
}

export default CoachingRequestFormWizard
